import React from 'react'
import { Link } from 'react-router-dom'
import {ArrowRight, DollarSign, Award, BookOpen} from 'lucide-react'

export default function HomePage() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.lumi.new/be/be5b7b4a24e3d69818425db7475032fa.png"
            alt="Premium Single Barrel Bourbon"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#3E2723]/95 via-[#3E2723]/70 to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-7xl font-bold text-[#FAF8F5] leading-tight mb-6">
              The Ultimate Guide to <span className="text-[#F59E0B]">Single Barrel Bourbon</span>
            </h1>
            <p className="text-xl text-[#FAF8F5]/90 mb-8 leading-relaxed">
              Honest reviews, community insights, and expert knowledge for whiskey single barrel enthusiasts.
            </p>
            <div className="flex space-x-4">
              <Link 
                to="/reviews" 
                className="bg-[#F59E0B] hover:bg-[#D97706] text-[#3E2723] px-8 py-4 rounded-lg font-bold transition-all transform hover:scale-105 hover:shadow-xl shadow-[#F59E0B]/30 flex items-center space-x-2"
              >
                <span>Read Reviews</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/specs" 
                className="bg-transparent border-2 border-[#B87333] hover:bg-[#B87333]/10 text-[#FAF8F5] px-8 py-4 rounded-lg font-bold transition-all flex items-center space-x-2"
              >
                <span>Check Prices</span>
                <DollarSign className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What is Single Barrel Section */}
      <section className="py-20 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#3E2723] mb-6">
                What is Single Barrel Bourbon?
              </h2>
              <div className="space-y-4 text-[#3E2723]/80 text-lg leading-relaxed">
                <p>
                  <strong className="text-[#D97706]">Single barrel bourbon</strong> represents the pinnacle of craft whiskey production. Unlike blended bourbons that mix multiple barrels for consistency, each single barrel bottle comes from one exceptional cask, offering a unique flavor profile that will never be exactly replicated.
                </p>
                <p>
                  Every <strong>whiskey single barrel</strong> expression tells its own story—influenced by its position in the rickhouse, the char level of the oak, seasonal temperature variations, and the master distiller's careful selection process.
                </p>
                <p>
                  This means when you buy a <strong>single barrel</strong> bourbon, you're getting something truly one-of-a-kind. The bottle in your hand may taste subtly different from another bottle of the same brand—and that's exactly what makes these expressions so prized by collectors and enthusiasts.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://static.lumi.new/0c/0cf71d36ee750e33e24fa5200512bf10.png"
                alt="Single Barrel Bourbon Pour"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#3E2723] text-[#F59E0B] p-6 rounded-xl shadow-xl border border-[#B87333]/30">
                <p className="text-4xl font-bold">100%</p>
                <p className="text-[#FAF8F5] text-sm">From One Barrel</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Economics of Bourbon */}
      <section className="py-20 bg-[#3E2723]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-[#FAF8F5] mb-12 text-center">
            The Economics of Bourbon
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-[#4E342E] p-8 rounded-xl border border-[#B87333]/20 hover:border-[#B87333]/50 transition-all">
              <h3 className="text-2xl font-bold text-[#F59E0B] mb-4">Cost of a Barrel of Bourbon</h3>
              <p className="text-[#FAF8F5]/80 leading-relaxed mb-4">
                A full 53-gallon bourbon barrel from a major distillery typically costs between <strong className="text-[#F59E0B]">$8,000 to $15,000</strong> at wholesale, depending on age and distillery reputation. After evaporation (the "angel's share"), a barrel yields approximately 150-200 bottles.
              </p>
              <p className="text-[#FAF8F5]/60 text-sm">
                This means each bottle carries a base cost of $40-100 before bottling, distribution, and retail markup.
              </p>
            </div>

            <div className="bg-[#4E342E] p-8 rounded-xl border border-[#B87333]/20 hover:border-[#B87333]/50 transition-all">
              <h3 className="text-2xl font-bold text-[#F59E0B] mb-4">Ex-Bourbon Barrels</h3>
              <p className="text-[#FAF8F5]/80 leading-relaxed mb-4">
                Once emptied, <strong className="text-[#F59E0B]">ex bourbon barrels</strong> are highly sought after by Scotch, rum, and craft beer producers. These used barrels sell for <strong className="text-[#F59E0B]">$100-300</strong> each and impart residual bourbon flavors to aging spirits.
              </p>
              <p className="text-[#FAF8F5]/60 text-sm">
                The secondary market for bourbon barrels represents a significant revenue stream for American distilleries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Reviews Preview */}
      <section className="py-20 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-4xl font-bold text-[#3E2723]">Community Consensus Reviews</h2>
            <Link to="/reviews" className="text-[#D97706] hover:text-[#B87333] font-medium flex items-center space-x-2 group">
              <span>View All</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "Jim Beam Single Barrel Reddit", subtitle: "What Reddit Actually Thinks", rating: "7.5/10" },
              { title: "Evan Williams Single Barrel", subtitle: "The Reddit Consensus", rating: "8.2/10" },
              { title: "1792 Single Barrel Worth It?", subtitle: "Breaking Bourbon Analysis", rating: "8.8/10" }
            ].map((review, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-lg overflow-hidden border border-[#B87333]/10 hover:border-[#B87333]/30 transition-all hover:shadow-xl hover:-translate-y-1 group">
                <div className="h-48 bg-gradient-to-br from-[#D97706] to-[#B87333]"></div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="bg-[#F59E0B]/10 text-[#D97706] px-3 py-1 rounded-full text-sm font-bold">
                      {review.rating}
                    </span>
                    <Award className="w-5 h-5 text-[#B87333]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#3E2723] mb-2 group-hover:text-[#D97706] transition-colors">
                    {review.title}
                  </h3>
                  <p className="text-[#3E2723]/60 text-sm mb-4">{review.subtitle}</p>
                  <Link to="/reviews" className="text-[#D97706] font-medium text-sm flex items-center space-x-1 hover:text-[#B87333]">
                    <span>Read Full Review</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#3E2723] mb-12 text-center">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="border border-[#B87333]/20 rounded-xl p-6 hover:border-[#B87333]/50 transition-all">
              <h3 className="text-xl font-bold text-[#3E2723] mb-3 flex items-center space-x-2">
                <BookOpen className="w-6 h-6 text-[#D97706]" />
                <span>How much does a barrel of bourbon cost?</span>
              </h3>
              <p className="text-[#3E2723]/70 leading-relaxed">
                A full barrel of bourbon from a commercial distillery ranges from $8,000 to $15,000 depending on the age statement and brand prestige. Private barrel picks for retailers typically fall in the $10,000-$12,000 range for 4-8 year aged bourbon.
              </p>
            </div>

            <div className="border border-[#B87333]/20 rounded-xl p-6 hover:border-[#B87333]/50 transition-all">
              <h3 className="text-xl font-bold text-[#3E2723] mb-3 flex items-center space-x-2">
                <BookOpen className="w-6 h-6 text-[#D97706]" />
                <span>What are ex bourbon barrels used for?</span>
              </h3>
              <p className="text-[#3E2723]/70 leading-relaxed">
                Ex bourbon barrels are primarily sold to Scotch whisky distilleries in Scotland, where they're used for aging single malt and blended Scotch. They're also popular in rum production, craft beer barrel-aging programs, and even furniture making. The charred oak imparts subtle bourbon sweetness to whatever is aged inside.
              </p>
            </div>

            <div className="border border-[#B87333]/20 rounded-xl p-6 hover:border-[#B87333]/50 transition-all">
              <h3 className="text-xl font-bold text-[#3E2723] mb-3 flex items-center space-x-2">
                <BookOpen className="w-6 h-6 text-[#D97706]" />
                <span>What makes single barrel bourbon different from regular bourbon?</span>
              </h3>
              <p className="text-[#3E2723]/70 leading-relaxed">
                Single barrel bourbon comes from one individual barrel rather than being blended from multiple barrels. This creates unique flavor variations between bottles and showcases the distiller's ability to select exceptional barrels. Each barrel develops its own character based on warehouse location, wood grain, and aging conditions.
              </p>
            </div>

            <div className="border border-[#B87333]/20 rounded-xl p-6 hover:border-[#B87333]/50 transition-all">
              <h3 className="text-xl font-bold text-[#3E2723] mb-3 flex items-center space-x-2">
                <BookOpen className="w-6 h-6 text-[#D97706]" />
                <span>Where can I buy single barrel bourbon for sale?</span>
              </h3>
              <p className="text-[#3E2723]/70 leading-relaxed">
                Single barrel bourbon for sale can be found at specialty liquor stores, Total Wine locations, and some warehouse clubs like Costco (Kirkland Single Barrel). Online retailers like ReserveBar and Caskers also carry allocated single barrel releases. Check our <Link to="/specs" className="text-[#D97706] hover:underline font-medium">Bottle Specs</Link> section for current pricing and availability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#3E2723] via-[#4E342E] to-[#3E2723]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#FAF8F5] mb-6">
            Start Your Single Barrel Journey
          </h2>
          <p className="text-xl text-[#FAF8F5]/80 mb-10 leading-relaxed">
            Explore our comprehensive database of reviews, specs, and historical insights.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/reviews" className="bg-[#4E342E] hover:bg-[#5D4037] border border-[#B87333]/30 p-8 rounded-xl transition-all hover:shadow-2xl hover:shadow-[#F59E0B]/20 group">
              <Award className="w-12 h-12 text-[#F59E0B] mb-4 mx-auto group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-[#FAF8F5] mb-2">Community Reviews</h3>
              <p className="text-[#FAF8F5]/60 text-sm">Real opinions from Reddit & enthusiast forums</p>
            </Link>
            
            <Link to="/specs" className="bg-[#4E342E] hover:bg-[#5D4037] border border-[#B87333]/30 p-8 rounded-xl transition-all hover:shadow-2xl hover:shadow-[#F59E0B]/20 group">
              <DollarSign className="w-12 h-12 text-[#F59E0B] mb-4 mx-auto group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-[#FAF8F5] mb-2">Bottle Specs & Pricing</h3>
              <p className="text-[#FAF8F5]/60 text-sm">Detailed specs, proofs, UPCs & availability</p>
            </Link>
            
            <Link to="/history" className="bg-[#4E342E] hover:bg-[#5D4037] border border-[#B87333]/30 p-8 rounded-xl transition-all hover:shadow-2xl hover:shadow-[#F59E0B]/20 group">
              <BookOpen className="w-12 h-12 text-[#F59E0B] mb-4 mx-auto group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-[#FAF8F5] mb-2">History & Education</h3>
              <p className="text-[#FAF8F5]/60 text-sm">Deep dives into distillery heritage & legends</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
